import { Link } from 'react-router-dom';
import { Plus, Share2 } from 'lucide-react';
import { getGameById } from '@/config/games';
import { useGameStore } from '@/stores/game.store';
import { useAuthStore } from '@/stores/auth.store';
import { useModuleEnabled } from '@/stores/projectPages.store';
import { useGameGroupsStore } from '@/stores/gameGroups.store';
import { GRADE_SETTINGS_FALLBACK } from '@/config/panelModules';
import { GradeApparence } from '@/components/config/GradeApparence';
import { GradeEssentials } from '@/components/config/GradeEssentials';
import { GradeBoutique } from '@/components/config/GradeBoutique';
import styles from '@/pages/GradesPage.module.scss';

// ============================================================
// Cosmétiques d'un grade, réglés depuis Supervision > Grades
// ============================================================
// Ornement, rank tag et rendu du chat d'un grade in-game. Les options proposées (ornements, rank
// tags) sont le catalogue créé dans Configuration > Cosmétiques des grades ; le choix d'un grade
// s'écrit dans la config de TOUS les containers du projet (cf AllContainers), commune à tous les
// groupes de jeu : il ne dépend pas du groupe choisi dans le bandeau.
// ============================================================

/**
 * Où se règle le choix de chaque grade (ornement, rank tag, chat, boutique) sur le projet actif, ou
 * null sans accès. Garde d'avant le déplacement : droit config.grades et module « cosmetics ». Sa
 * place est le détail d'un grade de Supervision > Grades ; module « grades » coupé pour le projet
 * (page fermée), Configuration > Cosmétiques des grades le reprend (GRADE_SETTINGS_FALLBACK).
 */
export function useGradeSettingsPlace(): 'supervision' | 'configuration' | null {
  const hasPage = useAuthStore((s) => s.hasPage);
  const isModuleOn = useModuleEnabled();
  if (!hasPage('config.grades') || !isModuleOn('cosmetics')) return null;
  return isModuleOn(GRADE_SETTINGS_FALLBACK.module) ? 'supervision' : 'configuration';
}

/**
 * Jeu à plusieurs groupes : ces réglages écrivent dans les containers de configuration, communs à
 * tous les groupes (Nova et Iris), alors que la page Grades montre le LuckPerms d'un seul. Rien
 * pour un jeu sans groupes ni pour un jeu à un seul groupe. Un groupe sans satellite (lobby) ni un
 * groupe retiré ne comptent pas, comme dans le sélecteur du bandeau.
 */
export function SharedAcrossGroupsNote() {
  const gameId = useGameStore((s) => s.activeGameId);
  const entry = useGameGroupsStore((s) => s.entries[gameId]);
  const labels = (entry?.groups ?? [])
    .filter((g) => !g.retired && (g.available || g.capabilities.length > 0))
    .map((g) => g.label);
  if (labels.length < 2) return null;
  const list = `${labels.slice(0, -1).join(', ')} et ${labels[labels.length - 1]}`;
  return (
    <div className={styles.sharedNote}>
      <Share2 size={14} aria-hidden />
      <span>
        Commun à {list} : ces réglages vivent dans les containers de configuration de {getGameById(gameId).name},
        partagés par tous les groupes. Les modifier ici les change aussi pour les autres groupes.
      </span>
    </div>
  );
}

/**
 * Onglet « Cosmétiques » du détail d'un grade : ornement et rank tag (listes déroulantes alimentées
 * par le catalogue du pack), puis rendu du chat avec son aperçu. Chaque bloc garde sa lecture et
 * son enregistrement (barre du bas) dans tous les containers du projet. `catalogLink` à false :
 * l'écran montre déjà le catalogue (repli dans Configuration), le lien vers lui n'a pas lieu d'être.
 */
export function GradeCosmetics({ grade, readOnly, catalogLink = true }: {
  grade: string;
  readOnly: boolean;
  catalogLink?: boolean;
}) {
  const hasPage = useAuthStore((s) => s.hasPage);
  const isModuleOn = useModuleEnabled();
  // Créer une option (ornement, rank tag) : le catalogue de Configuration, sous son propre droit.
  const canCatalog = catalogLink && hasPage('config.cosmetics') && isModuleOn('cosmetics');

  return (
    <>
      <SharedAcrossGroupsNote />
      <GradeApparence grade={grade} readOnly={readOnly} />
      <GradeEssentials grade={grade} readOnly={readOnly} />
      {canCatalog && (
        <Link to="/config/cosmetics" className={styles.crossLink} style={{ alignSelf: 'flex-start' }}
          title="Créer un ornement, un rank tag ou des glyphes : Configuration > Cosmétiques des grades">
          <Plus size={14} /> Créer une option : Configuration &gt; Cosmétiques des grades
        </Link>
      )}
    </>
  );
}

/** Onglet d'un grade dont les réglages s'écrivent dans les containers (hors Permissions). */
export type GradeSettingsTab = 'cosmetiques' | 'boutique';

/**
 * Contenu de l'onglet Cosmétiques ou Boutique d'un grade : le même dans le détail d'un grade de
 * Supervision > Grades et dans le repli de Configuration > Cosmétiques des grades.
 */
export function GradeSettingsPanel({ grade, tab, readOnly, catalogLink = true }: {
  grade: string;
  tab: GradeSettingsTab;
  readOnly: boolean;
  catalogLink?: boolean;
}) {
  if (tab === 'cosmetiques') return <GradeCosmetics grade={grade} readOnly={readOnly} catalogLink={catalogLink} />;
  return (
    <>
      <SharedAcrossGroupsNote />
      <GradeBoutique grade={grade} readOnly={readOnly} />
    </>
  );
}
